//
// Copyright (c) 2025 Comcast
//

import { Selection, TextSelection, NodeSelection } from '@tiptap/pm/state'
import { CellSelection } from '@tiptap/pm/tables'

// ProseMirror cells start at the cell node boundary (non-textblock).
// +1 keeps the cursor at the boundary and can break TextSelection (e.g., table_header).
// +2 places the caret inside the first paragraph in the cell, making selection valid.
const CELL_CONTENT_OFFSET = 2

/**
 * Normalize current selection to a valid text position inside a table cell or container node.
 * - If CellSelection: place caret inside the anchor cell's first textblock
 * - If NodeSelection: move to nearest text position
 * Returns the updated selection (may be unchanged)
 */
export const normalizeSelectionToTextPosition = (view) => {
  if (!view) return null

  const selection = view.state.selection

  // Handle table CellSelection
  if (selection instanceof CellSelection) {
    const anchorCell = selection.$anchorCell
    if (anchorCell) {
      const posInside = anchorCell.pos + CELL_CONTENT_OFFSET
      const $pos = view.state.doc.resolve(posInside)
      if (!$pos.parent.isTextblock) {
        const found = Selection.findFrom($pos, 1, true) || Selection.findFrom($pos, -1, true)
        if (found) {
          view.dispatch(view.state.tr.setSelection(found))
        }
      } else {
        const textSel = TextSelection.create(view.state.doc, posInside)
        view.dispatch(view.state.tr.setSelection(textSel))
      }
    }
    return view.state.selection
  }

  // Handle NodeSelection: move into nearest text position
  if (selection instanceof NodeSelection) {
    const $from = view.state.doc.resolve(selection.from)
    const found = Selection.findFrom($from, 1, true) || Selection.findFrom($from, -1, true)
    if (found) {
      view.dispatch(view.state.tr.setSelection(found))
    }
    return view.state.selection
  }

  return selection
}
